import { useState, useEffect, useRef, useCallback } from 'react';
import { EXIT_INTENT_SHOWN_KEY } from '../lib/batchSimulatorStorageBridge';

export interface UseExitIntentOptions {
  enabled?: boolean;
  isDirty: boolean;
  isExemptUser?: boolean;
  isExcluded?: boolean;
  sessionStorageKey?: string;
  onTrigger?: (source: 'mouse_leave' | 'navigation') => void;
}

export interface UseExitIntentReturn {
  isExitModalOpen: boolean;
  showModal: boolean;
  setIsExitModalOpen: (open: boolean) => void;
  openExitModal: (source?: 'mouse_leave' | 'navigation') => void;
  closeModal: () => void;
  resetExitIntent: () => void;
  discardAndProceed: () => void;
  cancelExit: () => void;
  handleInternalNavigation: (navigateCallback: () => void, targetPath?: string) => boolean;
  pendingNavigationPath: string | null;
}

export function useExitIntent({
  enabled = true,
  isDirty,
  isExemptUser = false,
  isExcluded = false,
  sessionStorageKey = EXIT_INTENT_SHOWN_KEY,
  onTrigger,
}: UseExitIntentOptions): UseExitIntentReturn {
  const isExempt = isExemptUser || isExcluded;
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const pendingNavigationCallbackRef = useRef<(() => void) | null>(null);

  const openExitModal = useCallback((source: 'mouse_leave' | 'navigation' = 'mouse_leave') => {
    if (!enabled || !isDirty || isExempt) return;

    try {
      const alreadyShown = sessionStorage.getItem(sessionStorageKey);
      if (alreadyShown === 'true') {
        return;
      }
      sessionStorage.setItem(sessionStorageKey, 'true');
    } catch {}

    setIsExitModalOpen(true);
    onTrigger?.(source);
  }, [enabled, isDirty, isExempt, sessionStorageKey, onTrigger]);

  const closeModal = useCallback(() => {
    setIsExitModalOpen(false);
  }, []);

  const resetExitIntent = useCallback(() => {
    try {
      sessionStorage.removeItem(sessionStorageKey);
    } catch {}
  }, [sessionStorageKey]);

  // Gatilho Desktop: mouseleave quando cursor atinge o topo da janela (clientY <= 0)
  useEffect(() => {
    if (!enabled || !isDirty || isExempt) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Ignora se o cursor saiu por baixo, laterais ou se foi clique
      if (e.clientY <= 0) {
        openExitModal('mouse_leave');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, isDirty, isExempt, openExitModal]);

  // Proteção de fechamento/recarregamento acidental de aba (beforeunload)
  useEffect(() => {
    if (!enabled || !isDirty || isExempt) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, isDirty, isExempt]);

  // Interceptador de navegação interna
  const handleInternalNavigation = useCallback((navigateCallback: () => void, targetPath?: string): boolean => {
    if (!enabled || !isDirty || isExempt) {
      navigateCallback();
      return false;
    }

    try {
      const alreadyShown = sessionStorage.getItem(sessionStorageKey);
      if (alreadyShown === 'true') {
        navigateCallback();
        return false;
      }
    } catch {}

    pendingNavigationCallbackRef.current = navigateCallback;
    setPendingNavigationPath(targetPath || null);
    openExitModal('navigation');
    return true; // Interceptou
  }, [enabled, isDirty, isExemptUser, sessionStorageKey, openExitModal]);

  const discardAndProceed = useCallback(() => {
    setIsExitModalOpen(false);
    if (pendingNavigationCallbackRef.current) {
      const cb = pendingNavigationCallbackRef.current;
      pendingNavigationCallbackRef.current = null;
      setPendingNavigationPath(null);
      cb();
    }
  }, []);

  const cancelExit = useCallback(() => {
    setIsExitModalOpen(false);
    pendingNavigationCallbackRef.current = null;
    setPendingNavigationPath(null);
  }, []);

  return {
    isExitModalOpen,
    showModal: isExitModalOpen,
    setIsExitModalOpen,
    openExitModal,
    closeModal,
    resetExitIntent,
    discardAndProceed,
    cancelExit,
    handleInternalNavigation,
    pendingNavigationPath,
  };
}
